import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { fmtGHS, fmtDateTime } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { AdmitDialog } from "@/components/inpatients/AdmitDialog";
import { InpatientRecord, IP_STATUS } from "@/components/inpatients/InpatientRecord";
import { BedDouble, Plus, Search } from "lucide-react";
import { toast } from "sonner";

export default function Inpatients() {
  const { can } = useAuth();
  const { branchParam } = useBranch();
  const [status, setStatus] = useState("admitted");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [admitOpen, setAdmitOpen] = useState(false);
  const [open, setOpen] = useState(null);
  const [products, setProducts] = useState([]);
  const load = useCallback(() => {
    api.get("/inpatients", { params: { status, q: q || undefined, ...(branchParam ? { branch_id: branchParam } : {}) } }).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [status, q, branchParam]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  const openRec = async (id) => { try { setOpen((await api.get(`/inpatients/${id}`)).data); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <PageHeader title="Inpatients" subtitle="Running medication tab for admitted patients — settled at the pharmacy on discharge."
        actions={can("inpatients.admit") && <Button onClick={() => setAdmitOpen(true)} data-testid="ip-admit-btn"><Plus className="h-4 w-4 mr-1.5" /> Admit patient</Button>} />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <Tabs value={status} onValueChange={setStatus}><TabsList>
          <TabsTrigger value="admitted" data-testid="ip-tab-admitted">Admitted</TabsTrigger>
          <TabsTrigger value="discharged" data-testid="ip-tab-discharged">Discharged – balance due</TabsTrigger>
          <TabsTrigger value="settled" data-testid="ip-tab-settled">Settled</TabsTrigger>
        </TabsList></Tabs>
        <div className="relative"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8 w-64 h-9" placeholder="File number or patient name" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ip-search" /></div>
      </div>
      <Card className="shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr><th className="py-2.5 px-3">File no.</th><th className="py-2.5 px-3">Patient</th><th className="py-2.5 px-3">Branch</th><th className="py-2.5 px-3">Payment</th>
              <th className="py-2.5 px-3">Admitted</th><th className="py-2.5 px-3 text-right">Medicines</th><th className="py-2.5 px-3 text-right">Charged</th><th className="py-2.5 px-3 text-right">Balance</th><th className="py-2.5 px-3">Status</th></tr>
          </thead>
          <tbody>
            {!rows.length && <tr><td colSpan={9} className="py-12 text-center text-muted-foreground"><BedDouble className="h-8 w-8 mx-auto mb-2 opacity-40" />No patients here</td></tr>}
            {rows.map((a) => { const [tone, label] = IP_STATUS[a.status] || []; return (
              <tr key={a.id} className="border-t border-border hover:bg-muted/50 cursor-pointer" onClick={() => openRec(a.id)} data-testid={`ip-row-${a.file_no}`}>
                <td className="py-2 px-3 font-mono">{a.file_no}</td><td className="py-2 px-3 font-medium">{a.patient_name}<div className="text-[11px] text-muted-foreground">{a.code}{a.ward ? ` · ${a.ward}` : ""}{a.bed ? ` / ${a.bed}` : ""}</div></td>
                <td className="py-2 px-3">{a.branch_name}</td><td className="py-2 px-3 text-xs">{a.payer_type === "insurance" ? a.scheme_name : a.payer_type === "corporate" ? a.customer_name : "Self-pay"}</td>
                <td className="py-2 px-3 text-xs text-muted-foreground">{fmtDateTime(a.admitted_at)}</td><td className="py-2 px-3 text-right font-mono">{a.tab.items}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(a.tab.total)}</td><td className="py-2 px-3 text-right font-mono">{fmtGHS(a.tab.balance)}</td>
                <td className="py-2 px-3"><StatusBadge value={tone} label={label} /></td>
              </tr>); })}
          </tbody>
        </table>
      </Card>
      <AdmitDialog open={admitOpen} onOpenChange={setAdmitOpen} onAdmitted={(d) => { load(); setOpen(d); }} />
      <InpatientRecord a={open} setA={setOpen} products={products} onChanged={load} />
    </div>
  );
}
